import React, { useEffect, useState } from "react";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import {
  createNotificationSettings,
  updateNotificationSettings,
} from "../../api/SettingsApi";
import { INotificationSettings } from "../../exports/interfaces";
import { usePermission } from "../../hooks/usePermission";

interface NotificationSettingProps {
  notificationSettings: INotificationSettings | null;
  setNotificationSettings: React.Dispatch<React.SetStateAction<INotificationSettings | null>>;
}

const NotificationSetting = ({
  notificationSettings,
  setNotificationSettings,
}: NotificationSettingProps) => {
  const { setLoading } = useLoader();
    const { canEdit } = usePermission();
  const [notifications, setNotifications] = useState<INotificationSettings>({
    email: false,
    sms: false,
    inApp: false,

    newApplication: false,
    applicationStatus: false,
    complaintUpdate: false,
    noticePublished: false,
    documentVerification: false,
  });

  const handleToggle = (key: keyof INotificationSettings) => {
    const updated = {
      ...notifications,
      [key]: !notifications[key],
    } as INotificationSettings;
    setNotifications(updated);
    setNotificationSettings(updated);
  };

  const notificationChannels = [
    {
      key: "email",
      title: "Email Notifications",
      description: "Receive notifications via email.",
    },
    {
      key: "sms",
      title: "SMS Notifications",
      description: "Receive critical alerts via SMS.",
    },
    {
      key: "inApp",
      title: "In-App Notifications",
      description: "Show toast and bell-icon updates inside the app.",
    },
  ];

  const notificationEvents = [
    {
      key: "newApplication",
      title: "New application submitted",
    },
    {
      key: "applicationStatus",
      title: "Application status changed",
    },
    {
      key: "complaintUpdate",
      title: "Complaint filed or updated",
    },
    {
      key: "noticePublished",
      title: "Notice published",
    },
    {
      key: "documentVerification",
      title: "Document verification result",
    },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = notifications;

      const response = notificationSettings?.id
        ? await updateNotificationSettings(notificationSettings.id, payload)
        : await createNotificationSettings(payload);

      if (response.status === 200 || response.status === 201) {
        setNotificationSettings(response.data);
        setNotifications(response.data);
        dynamicSuccess(
          notificationSettings
            ? "Notification settings updated successfully!"
            : "Notification settings saved successfully!",
        );
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (notificationSettings) {
      setNotifications(notificationSettings);
    }
  }, [notificationSettings]);

  return (
    <>
      <div className={`dashboard-card`} style={{ borderTopLeftRadius: 0 }}>
        <div className="row g-3">
          <div className="col-12">
            {/* Notification Channels */}
            <h5 className="mb-1">Notification Preferences</h5>
            <small className="text-secondary">
              Choose how you want to be notified about activity.
            </small>

            <div className="row mt-2">
              {notificationChannels.map((item) => (
                <div className="col-md-6">
                  <div
                    key={item.key}
                    className="d-flex justify-content-between align-items-center py-3 border-bottom"
                  >
                    <div>
                      <label className="form-label d-block">{item.title}</label>
                      <small className="setting-description">{item.description}</small>
                    </div>

                    <div className="form-check form-switch notification-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          (notifications[item.key as keyof INotificationSettings] as boolean) ??
                          false
                        }
                        onChange={() =>
                          handleToggle(item.key as keyof INotificationSettings)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Events */}
      <div className={`dashboard-card mt-2`}>
        <div className="row g-3">
          <div className="col-12">
            <div className="row">
              {notificationEvents.map((item) => (
                <div className="col-md-6">
                  <div
                    key={item.key}
                    className="d-flex justify-content-between align-items-center py-3 border-bottom"
                  >
                    <span>{item.title}</span>

                    <div className="form-check form-switch notification-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          (notifications[item.key as keyof INotificationSettings] as boolean) ??
                          false
                        }
                        onChange={() =>
                          handleToggle(item.key as keyof INotificationSettings)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <button
                  className="btn btn-badge-primary px-3"
                  onClick={handleSubmit}
                  disabled={!canEdit("Settings")}
                  style={{
                    pointerEvents: canEdit("Settings") ? "auto" : "none",
                    opacity: canEdit("Settings") ? 1 : 0.5,
                  }}
                >
                Send Change
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationSetting;
